<template>
  <v-card class="my-4">
    <v-card-title>
      Marketing Campaigns
      <v-spacer />
      <v-btn
        icon
        class="mr-2"
        title="Refresh"
        :loading="refreshing"
        @click="refresh"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
      <v-btn
        icon
        class="mr-2"
        title="Settings"
        @click="$refs.settings.open()"
      >
        <v-icon>mdi-cog</v-icon>
      </v-btn>
      <v-btn
        color="secondary"
        outlined
        class="mr-2"
        :loading="filling"
        title="Plan ahead with AI-suggested campaigns"
        @click="$refs.fillDialog.open()"
      >
        <v-icon left>
          mdi-calendar-month-outline
        </v-icon>
        Fill calendar
      </v-btn>
      <v-btn
        color="secondary"
        class="mr-2"
        @click="suggestDrawer = true"
      >
        <v-icon left>
          mdi-lightbulb-on-outline
        </v-icon>
        Suggest a campaign
      </v-btn>
      <v-menu offset-y>
        <template #activator="{ on, attrs }">
          <v-btn
            color="primary"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon left>
              mdi-plus
            </v-icon>
            Add
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item @click="add($enums.MARKETING.CAMPAIGN_TYPE.EMAIL)">
            <v-list-item-title>Email campaign</v-list-item-title>
          </v-list-item>
          <v-list-item @click="add($enums.MARKETING.CAMPAIGN_TYPE.SOCIAL)">
            <v-list-item-title>Social post</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-card-title>

    <v-card-text class="pb-0 d-flex flex-wrap align-center">
      <v-btn-toggle
        v-model="typeFilter"
        color="mcsprimary"
        mandatory
        dense
        class="mr-4 mb-2"
      >
        <v-btn :value="0">
          All
        </v-btn>
        <v-btn :value="$enums.MARKETING.CAMPAIGN_TYPE.EMAIL">
          Email
        </v-btn>
        <v-btn :value="$enums.MARKETING.CAMPAIGN_TYPE.SOCIAL">
          Social
        </v-btn>
      </v-btn-toggle>

      <v-autocomplete
        v-model="audienceFilter"
        :items="audienceItems"
        item-text="name"
        item-value="key"
        label="Audience"
        placeholder="All audiences"
        prepend-inner-icon="mdi-account-search-outline"
        dense
        outlined
        clearable
        hide-details
        style="max-width: 340px;"
        class="mb-2"
      />

      <!-- List only: the calendar is a record of what went out, so hiding sent campaigns there would leave holes. -->
      <v-select
        v-if="tab === 0"
        v-model="statusFilter"
        :items="statusItems"
        label="Status"
        placeholder="All statuses"
        prepend-inner-icon="mdi-flag-outline"
        multiple
        dense
        outlined
        clearable
        hide-details
        style="max-width: 300px;"
        class="ml-4 mb-2"
      >
        <template #selection="{ item, index }">
          <v-chip
            v-if="index < 2"
            small
            :color="item.color"
            dark
            class="mr-1"
          >
            {{ item.text }}
          </v-chip>
          <span
            v-else-if="index === 2"
            class="text-caption grey--text"
          >
            +{{ statusFilter.length - 2 }}
          </span>
        </template>
      </v-select>

      <!-- Both tabs: unlike the status picker, reach is a property of the campaign rather than of its history,
           and the calendar is where "which weeks go out to every network" is actually legible. Every social
           campaign posts to Pinterest, so the useful question is how much further it goes — hence a reach
           picker rather than one checkbox per network. Emails match no reach, so choosing one narrows both
           surfaces to social posts. -->
      <v-select
        v-model="networkFilter"
        :items="networkItems"
        label="Networks"
        placeholder="Any reach"
        prepend-inner-icon="mdi-share-variant-outline"
        dense
        outlined
        clearable
        hide-details
        style="max-width: 220px;"
        class="ml-4 mb-2"
      />
    </v-card-text>

    <v-tabs v-model="tab">
      <v-tab>List</v-tab>
      <v-tab>Calendar</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <MarketingList
          :type-filter="typeFilter"
          :audience-filter="audienceFilter"
          :status-filter="statusFilter"
          :network-filter="networkFilter"
        />
      </v-tab-item>
      <v-tab-item>
        <MarketingCalendar
          :type-filter="typeFilter"
          :audience-filter="audienceFilter"
          :audience-name="audienceName"
          :network-filter="networkFilter"
        />
      </v-tab-item>
    </v-tabs-items>

    <Settings ref="settings" />

    <FillDialog
      ref="fillDialog"
      @confirm="fill"
    />

    <!-- Suggest a campaign -->
    <v-navigation-drawer
      v-model="suggestDrawer"
      right
      temporary
      fixed
      width="440"
    >
      <div
        class="pa-4 d-flex flex-column"
        style="height:100%;"
      >
        <div>
          <h3 class="mb-1">
            Suggest a campaign
          </h3>
          <p class="text-caption grey--text mb-4">
            Pick a direction or describe what you want — the AI builds a draft campaign from it.
          </p>

          <v-btn-toggle
            v-model="suggestType"
            color="mcsprimary"
            mandatory
            dense
            class="mb-4"
          >
            <v-btn
              small
              class="text-none"
              :value="$enums.MARKETING.CAMPAIGN_TYPE.EMAIL"
            >
              Email campaign
            </v-btn>
            <v-btn
              small
              class="text-none"
              :value="$enums.MARKETING.CAMPAIGN_TYPE.SOCIAL"
            >
              Social post
            </v-btn>
          </v-btn-toggle>

          <div class="text-caption grey--text mb-2">
            Quick ideas
          </div>
          <v-btn
            v-for="opt in suggestOptions"
            :key="opt"
            outlined
            block
            small
            class="mb-2 text-none justify-start"
            :disabled="suggesting"
            @click="runSuggest(opt)"
          >
            {{ opt }}
          </v-btn>
        </div>

        <v-spacer />

        <div>
          <v-divider class="mb-3" />
          <v-textarea
            v-model="suggestBrief"
            label="Or describe your own"
            placeholder="e.g. promote our wedding stationery to event planners"
            rows="3"
            outlined
            auto-grow
            hide-details
            class="mb-2"
            :disabled="suggesting"
          />
          <v-btn
            color="secondary"
            block
            :loading="suggesting"
            @click="runSuggest(suggestBrief)"
          >
            <v-icon left>
              mdi-lightbulb-on-outline
            </v-icon>
            Create campaign
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>
  </v-card>
</template>

<script>
import MarketingList from '@/components/marketing/campaigns/List';
import MarketingCalendar from '@/components/marketing/campaigns/Calendar';
import Settings from '@/components/marketing/campaigns/Settings';
import FillDialog from '@/components/marketing/campaigns/FillDialog';
import { mapGetters } from 'vuex';
import {
  audienceKey,
  campaignStatusOptions,
  defaultCampaignStatusFilter,
  networkReachOptions
} from '@/helpers/marketing';

export default {
  name: 'CampaignsIndex',
  components: { MarketingList, MarketingCalendar, Settings, FillDialog },
  data() {
    return {
      tab: 0,
      typeFilter: 0,
      audienceFilter: null,
      statusFilter: defaultCampaignStatusFilter(),
      networkFilter: null,
      refreshing: false,
      suggesting: false,
      filling: false,
      suggestDrawer: false,
      suggestBrief: '',
      suggestType: 1,
      suggestOptions: [
        "Promote a subject we haven't featured recently",
        'A seasonal or upcoming-holiday campaign',
        'Target a specific industry or profession',
        'Spotlight a popular product category'
      ]
    };
  },
  computed: {
    ...mapGetters({ audienceOptions: 'audiences/options' }),
    statusItems: campaignStatusOptions,
    networkItems: networkReachOptions,
    // The picker addresses an audience by the same (targetType, targetId) pair the audience screens do, flattened
    // into one key so v-autocomplete can hold it in a single value.
    audienceItems() {
      return this.audienceOptions.map((o) => ({
        key: audienceKey(o.targetType, o.id),
        name: o.name
      }));
    },
    audienceName() {
      const match = this.audienceItems.find((a) => a.key === this.audienceFilter);
      return match ? match.name : '';
    }
  },
  mounted() {
    // ?audience=<audienceKey> pre-filters the list — how the dashboard's "campaigns needing review" line
    // lands you on just this audience's campaigns. Read once; the picker owns it from then on.
    const audience = this.$route.query.audience;
    if (audience) this.audienceFilter = audience;

    this.$store.dispatch('audiences/getOptions').catch((error) => this.logError(error));
    // The network picker's "All networks" means "every ENABLED network", so the list and calendar need the
    // enabled set (they read it from the store getter this fills).
    this.$store.dispatch('campaigns/getSettings').catch((error) => this.logError(error));
  },
  methods: {
    refresh() {
      this.refreshing = true;
      this.$store
        .dispatch('campaigns/getList')
        .catch((error) => this.logError(error))
        .finally(() => {
          this.refreshing = false;
        });
    },
    add(type) {
      this.$router.push(`/marketing/campaigns/0?type=${type}`);
    },
    // `label` is what the dialog called the span ("2 weeks", "3 months"); the server only ever hears weeks.
    fill({ type, weeks, label, autoSchedule }) {
      this.filling = true;
      this.$store
        .dispatch('campaigns/fill', { type, weeks, autoSchedule })
        .then(() => {
          this.$root.$snackbarSuccess(
            autoSchedule
              ? `Filling ${label} — new draft campaigns will appear on the calendar over the next few minutes.`
              : `Building ${label} of campaigns — undated drafts will appear in the list over the next few minutes.`
          );
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.filling = false;
        });
    },
    runSuggest(brief) {
      this.suggesting = true;
      this.$store
        .dispatch('campaigns/suggest', { brief: (brief || '').trim(), type: this.suggestType })
        .then(() => {
          this.suggestDrawer = false;
          this.suggestBrief = '';
          this.$root.$snackbarSuccess(
            'Suggesting a campaign — it will appear on the list shortly. Use refresh to check.'
          );
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.suggesting = false;
        });
    }
  }
};
</script>
